'use client'

import { useEffect, useRef, useState, ReactNode } from 'react'

interface ScrollRevealProps {
    children: ReactNode
    direction?: 'up' | 'down' | 'left' | 'right' | 'none'
    delay?: number
    duration?: number
    className?: string
    distance?: number
    threshold?: number
}

const ScrollReveal = ({
    children,
    direction = 'up',
    delay = 0,
    className = '',
    threshold = 0.08,
}: ScrollRevealProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (delay) {
                        setTimeout(() => setVisible(true), delay)
                    } else {
                        setVisible(true)
                    }
                    observer.unobserve(el)
                }
            },
            { threshold }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [delay, threshold])

    const getDirectionClasses = () => {
        if (visible) return 'opacity-100 translate-x-0 translate-y-0'
        switch (direction) {
            case 'up':
                return 'opacity-0 translate-y-8'
            case 'down':
                return 'opacity-0 -translate-y-8'
            case 'left':
                return 'opacity-0 translate-x-8'
            case 'right':
                return 'opacity-0 -translate-x-8'
            case 'none':
                return 'opacity-0'
            default:
                return 'opacity-0 translate-y-8'
        }
    }

    if (!children) return null

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ease-out will-change-transform ${getDirectionClasses()} ${className}`}
        >
            {children}
        </div>
    )
}

export default ScrollReveal
